import React, { useCallback, useReducer, useState } from "react";
import { Box } from "@mui/material";

import Panel from "./Panel/Panel";
import {
  BannerStateType,
  ContentsType,
  CriticityType,
  DisplayDelayType,
  EditorErrorsType,
  FormErrorsType,
  LanguagesType,
  ReducerAction,
} from "./interfaces";
import Editors from "./Editor/Editors";
import { publishBanner, validateEditorContent, validateForm } from "./utils";
import { Locale, LOCALE_MAP_TRANSLATIONS } from "../Locale";
import { useMutation, useQueryClient } from "react-query";
import { useIntl } from "react-intl";
import { enqueueSnackbar } from "notistack";

export const REQUIRED_LANGAGE_ID = Locale.EN_GB;

export const PINNING_DEFAULT_VALUE = false;

export const initialState: BannerStateType = {
  customers: [],
  languages: [
    {
      id: REQUIRED_LANGAGE_ID,
      value: LOCALE_MAP_TRANSLATIONS[REQUIRED_LANGAGE_ID],
    },
  ],
  is_active: false,
  is_pinned: PINNING_DEFAULT_VALUE,
};

function reducer(state: BannerStateType, action: ReducerAction) {
  if (action.type === "languages") {
    const newLanguages = action.value;

    const filterContent = state.content
      ? Object.fromEntries(
          Object.entries(state.content).filter(([key]) =>
            newLanguages.some((lang) => lang.id === key)
          )
        )
      : {};

    return {
      ...state,
      languages: newLanguages,
      content: filterContent,
    };
  } else if (action.type === "customers") {
    return {
      ...state,
      customers: action.value,
    };
  } else if (action.type === "is_active") {
    return {
      ...state,
      is_active: action.value,
    };
  } else if (action.type === "criticity") {
    return {
      ...state,
      criticity: action.value,
    };
  } else if (action.type === "is_pinned") {
    if (action.value) {
      return {
        ...state,
        is_pinned: action.value,
        nb_days_before_redisplay: undefined,
      };
    }

    return {
      ...state,
      is_pinned: action.value,
    };
  } else if (action.type === "nb_days_before_redisplay") {
    return {
      ...state,
      nb_days_before_redisplay: action.value,
    };
  } else if (action.type === "content") {
    return {
      ...state,
      content: {
        ...state.content,
        [action.value.id]: action.value.content,
      },
    };
  } else if (action.type === "name") {
    return {
      ...state,
      name: action.value,
    };
  }

  throw Error("Unknown action.");
}

const BannerEditor = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [formErrors, setFormErrors] = useState<FormErrorsType>({
    hasError: false,
  });
  const [editorsErrors, setEditorsErrors] = useState<EditorErrorsType>({
    hasError: false,
    errors: {},
  });

  const publishBannerMutation = useMutation(
    (state: BannerStateType) => publishBanner(state),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("bannerList");
        enqueueSnackbar(intl.formatMessage({ id: "OPERATION_SUCCESS" }), {
          variant: "success",
        });
      },
      onError: () => {
        enqueueSnackbar(intl.formatMessage({ id: "OPERATION_FAILED" }), {
          variant: "error",
        });
      },
    }
  );

  const handleLanguageChange = useCallback((languages: LanguagesType[]) => {
    dispatch({ type: "languages", value: languages });
  }, []);

  const handleCustomerChange = useCallback((customers: number[] | "all") => {
    dispatch({ type: "customers", value: customers });
  }, []);

  const handleActiveChange = useCallback((active: boolean) => {
    dispatch({ type: "is_active", value: active });
  }, []);

  const handleCriticityChange = useCallback((criticity: CriticityType) => {
    dispatch({ type: "criticity", value: criticity });
  }, []);

  const handlePinningChange = useCallback((pinning: boolean) => {
    dispatch({ type: "is_pinned", value: pinning });
  }, []);

  const handleBannerDisplayDelayChange = useCallback(
    (delay: DisplayDelayType) => {
      dispatch({ type: "nb_days_before_redisplay", value: delay });
    },
    []
  );

  const handleContentsChange = useCallback((contents: ContentsType) => {
    dispatch({ type: "content", value: contents });
  }, []);

  const handleNameChange = useCallback((name: string) => {
    dispatch({ type: "name", value: name });
  }, []);

  const handleSubmit = useCallback(() => {
    // Check form errors
    const validateFormResult = validateForm(state);

    setFormErrors(validateFormResult);

    if (validateFormResult.hasError) {
      return;
    }

    // Check contents errors
    const validateEditors = validateEditorContent(state);

    setEditorsErrors({
      hasError: !validateEditors.valid,
      errors: validateEditors.errors,
    });

    if (!validateEditors.valid) {
      return;
    }

    publishBannerMutation.mutate(state);
  }, [state, publishBannerMutation]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Editors
        activeLanguages={state.languages}
        onContentsChange={handleContentsChange}
        contentsFilledErrors={formErrors.content}
        editorsErrors={editorsErrors}
      />
      <Panel
        bannerPublishing={publishBannerMutation.isLoading}
        formErrors={formErrors}
        isActive={state.is_active}
        isPinned={state.is_pinned}
        onActiveToggle={handleActiveChange}
        onBannerDisplayChange={handleBannerDisplayDelayChange}
        onCustomerChange={handleCustomerChange}
        onCriticityChange={handleCriticityChange}
        onLanguageChange={handleLanguageChange}
        onNameChange={handleNameChange}
        onPinningChange={handlePinningChange}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};

export default BannerEditor;
